<script setup lang="ts">
import { Link, router } from "@inertiajs/vue3"
import AppShell from "../Layout/AppShell.vue"
import { UiButton, UiDataTable } from "@/components"
import { useConfirmation } from "@/composables/useConfirmation"
import { useNotifications } from "@/composables/useNotifications"
import type { UiDataTableColumn } from "@/types/ui"

const { confirm } = useConfirmation()
const notify = useNotifications()

type MeasurementUnitRow = {
  id: number
  name: string
  symbol: string
  can_edit: boolean
  can_destroy: boolean
}

const props = defineProps<{
  measurement_units: MeasurementUnitRow[]
  query: {
    q?: string
    page: number
    per_page: number
  }
  pagination: {
    page: number
    perPage: number
    total: number
  }
  can_create: boolean
}>()

const columns: UiDataTableColumn[] = [
  { key: "name", label: "Nome" },
  { key: "symbol", label: "Sigla" },
  { key: "actions", label: "Acoes", align: "right" },
]

function fetchWithQuery(overrides: Record<string, string | number | ""> = {}) {
  const params = {
    q: String(overrides.q ?? props.query.q ?? ""),
    page: Number(overrides.page ?? props.pagination.page),
    per_page: Number(overrides.per_page ?? props.pagination.perPage),
  }

  router.get("/measurement_units", params, {
    preserveState: true,
    preserveScroll: true,
    replace: true,
  })
}

function updateSearch(value: string) {
  fetchWithQuery({ q: value, page: 1 })
}

function changePage(page: number) {
  fetchWithQuery({ page })
}

function changePerPage(perPage: number) {
  fetchWithQuery({ per_page: perPage, page: 1 })
}

async function destroyUnit(id: number) {
  const ok = await confirm({
    title: "Excluir unidade",
    message: "Deseja excluir esta unidade de medida?",
    type: "danger",
    confirmText: "Excluir",
  })
  if (!ok) return

  router.delete(`/measurement_units/${id}`, {
    preserveScroll: true,
    onSuccess: () => notify.success("Unidade excluida com sucesso"),
  })
}
</script>

<template>
  <AppShell>
    <div class="space-y-6">
      <section class="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h1 class="text-2xl font-semibold text-slate-900">Unidade de Medida</h1>
        <p class="mt-2 text-sm text-slate-600">Gerencie unidades globais usadas por todos os produtos.</p>
      </section>

      <UiDataTable
        :columns="columns"
        :rows="measurement_units"
        :search="query.q ?? ''"
        :pagination="pagination"
        @update:search="updateSearch"
        @page-change="changePage"
        @per-page-change="changePerPage"
      >
        <template #toolbar-actions>
          <Link
            v-if="can_create"
            href="/measurement_units/new"
            class="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
          >
            Nova unidade
          </Link>
        </template>

        <template #cell="{ row, column, value }">
          <div v-if="column.key === 'actions'" class="flex justify-end gap-2">
            <Link
              v-if="(row as MeasurementUnitRow).can_edit"
              :href="`/measurement_units/${(row as MeasurementUnitRow).id}/edit`"
              class="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-100"
            >
              Editar
            </Link>

            <UiButton v-if="(row as MeasurementUnitRow).can_destroy" size="sm" variant="danger" @click="destroyUnit((row as MeasurementUnitRow).id)">
              Excluir
            </UiButton>
          </div>

          <span v-else>{{ value || "-" }}</span>
        </template>
      </UiDataTable>
    </div>
  </AppShell>
</template>

s
